// CRM Dynamic Prompt Generator
// Focused on open opportunities, actions needed, and pipeline health

/**
 * Generate focused CRM prompts — 3 categories, 8 total prompts
 * @param {Object} _context - Current CRM context (reserved for future use)
 * @returns {Promise<Array>} - Array of generated prompts
 */
export const generateCRMPrompts = async (_context: any) => {
  return [
    // Pipeline (3 prompts)
    {
      id: 'closing-this-month',
      category: 'Pipeline',
      label: "What's closing this month?",
      prompt: "Which deals are closing this month? List top 5 by value with name, owner, days to close. Be brief.",
      icon: '📅'
    },
    {
      id: 'stalled-deals',
      category: 'Pipeline',
      label: 'Stalled or inactive deals',
      prompt: "Show stalled/inactive deals from the attention list. For each, suggest one next step. Be brief.",
      icon: '⏸️'
    },
    {
      id: 'pipeline-health',
      category: 'Pipeline',
      label: 'Pipeline health check',
      prompt: "Quick pipeline health: counts and values by stage, any gaps or concerns? Be brief.",
      icon: '🔍'
    },

    // Actions (3 prompts)
    {
      id: 'follow-up-today',
      category: 'Actions',
      label: 'What needs follow-up?',
      prompt: "What needs attention now? Show overdue tasks and deals needing follow-up. Top 5 only, be brief.",
      icon: '🔔'
    },
    {
      id: 'at-risk-deals',
      category: 'Actions',
      label: 'At-risk deals',
      prompt: "Which deals are at risk? Show attention deals with Cold/Inactive status or overdue close. Suggest one action each. Be brief.",
      icon: '⚠️'
    },
    {
      id: 'my-priorities',
      category: 'Actions',
      label: 'My priorities this week',
      prompt: "What should I prioritize this week from my deals and tasks? Top 5 actions, be brief.",
      icon: '✅'
    },

    // Insights (2 prompts)
    {
      id: 'pipeline-snapshot',
      category: 'Insights',
      label: 'Pipeline snapshot',
      prompt: "Quick pipeline snapshot: total value, count by stage, stale/overdue counts. Be brief.",
      icon: '📊'
    },
    {
      id: 'team-workload',
      category: 'Insights',
      label: 'Team workload',
      prompt: "Show team workload from the by-owner breakdown. Flag anyone overloaded. Be brief.",
      icon: '👥'
    }
  ];
};

/**
 * Organize prompts by category
 * @param {Array} prompts - Array of prompt objects
 * @returns {Object} - Prompts organized by category
 */
export const organizePromptsByCategory = (prompts: any[]) => {
  const organized: Record<string, any[]> = {};

  prompts.forEach(prompt => {
    const category = prompt.category || 'General';
    if (!organized[category]) {
      organized[category] = [];
    }
    organized[category].push(prompt);
  });

  return organized;
};

/**
 * Get prompt categories with display information
 * @param {Array} prompts - Array of prompts
 * @returns {Array} - Array of category objects
 */
export const getPromptCategories = (prompts: any[]) => {
  const categories = [...new Set(prompts.map(p => p.category))];

  const categoryInfo: Record<string, { order: number; icon: string; color: string }> = {
    'Pipeline': { order: 1, icon: '📊', color: '#3b82f6' },
    'Actions': { order: 2, icon: '✅', color: '#ef4444' },
    'Insights': { order: 3, icon: '💡', color: '#10b981' }
  };

  return categories
    .map(cat => ({
      name: cat,
      ...(categoryInfo[cat] || { order: 99, icon: '📄', color: '#6b7280' })
    }))
    .sort((a, b) => a.order - b.order);
};

export default {
  generateCRMPrompts,
  organizePromptsByCategory,
  getPromptCategories
};
